/**
 * SelectionContextPill — a dismissible chip rendered above the
 * CommandRail input when the writer has text selected in Word.
 *
 * Communicates: "your selected text will be sent as context with
 * your next message." The writer can dismiss it per-message by
 * clicking ×.
 *
 * SRP: pure presentation — no state, no Word.run, no effects.
 *      Caller owns capture + dismiss lifecycle.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  row: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    padding: "4px 12px",
    // Sits immediately above the inputRow — subtle bottom separator.
    borderBottom: `1px solid ${surfaceColors.line}`,
  },
  // Context pill in the "Asthra Conversation Rail" design: a soft tinted
  // pill with a round dismiss — the same family as the composer's attached-
  // asset chips, in brand purple.
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
    maxWidth: "100%",
    padding: "2px 3px 2px 9px",
    borderRadius: "14px",
    background: brandColors.light,
    border: `1px solid ${brandColors.light}`,
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: "18px",
    color: brandColors.primary,
    userSelect: "none",
  },
  icon: {
    fontSize: "12px",
    lineHeight: 1,
    flexShrink: 0,
    opacity: 0.8,
  },
  preview: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: inkColors.ink3,
    fontWeight: 500,
    fontStyle: "italic",
  },
  dismissBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "16px",
    height: "16px",
    padding: 0,
    border: "none",
    borderRadius: "50%",
    background: `${brandColors.primary}1F`,
    color: brandColors.primary,
    fontSize: "9px",
    lineHeight: 1,
    cursor: "pointer",
    "&:hover": {
      background: `${brandColors.primary}33`,
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "1px",
    },
  },
});

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SelectionContextPillProps {
  /** The selected text to preview (always non-empty). */
  text: string;
  /** Called when the writer clicks the × dismiss button. */
  onDismiss: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PREVIEW_LIMIT = 60;

export const SelectionContextPill: React.FC<SelectionContextPillProps> = ({ text, onDismiss }) => {
  const styles = useStyles();
  const preview = text.length > PREVIEW_LIMIT ? `${text.slice(0, PREVIEW_LIMIT)}\u2026` : text;

  return (
    <div className={styles.row} aria-label="Selected text will be sent as context">
      <span className={styles.pill} title={text}>
        <span className={styles.icon} aria-hidden="true">
          ❝
        </span>
        <span className={styles.preview}>{preview}</span>
        <button
          type="button"
          className={styles.dismissBtn}
          onClick={onDismiss}
          aria-label="Remove selection context"
          title="Remove — message will be sent without selection context"
        >
          ✕
        </button>
      </span>
    </div>
  );
};
